import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { ExecutiveGuideBanner } from "./ExecutiveGuideBanner";

const LINKS = [
  { to: "/how-bos-works", label: "How BOS Works" },
  { to: "/bos-reveal", label: "BOS Reveal" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const DARK_HEADER_ROUTES = new Set(["/", "/about"]);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const darkHeader = scrolled || open || DARK_HEADER_ROUTES.has(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,color] duration-500 ${
          darkHeader
            ? `${open ? "bg-ink" : "bg-ink/85 backdrop-blur-md"} text-paper border-b border-paper/10`
            : "bg-transparent text-ink border-b border-transparent"
        }`}
      >
        <ExecutiveGuideBanner />
        <div className="max-w-7xl mx-auto px-6 md:px-10 xl:px-12 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center group shrink-0">
            <img
              src={darkHeader ? "/assets/logo-horizontal-reverse.png" : "/assets/logo-horizontal.png"}
              alt="BOS — Behaviour Operating System"
              width="1834"
              height="444"
              decoding="async"
              className="h-8 md:h-10 lg:h-11 w-auto transition-opacity duration-300 group-hover:opacity-70"
            />
          </Link>
          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-rule whitespace-nowrap text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive
                      ? "text-terra"
                      : darkHeader
                      ? "text-paper/75 hover:text-paper"
                      : "text-ink/80 hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/operational-clarity-review"
              data-testid="nav-cta-clarity-review"
              className="ml-1 inline-flex items-center gap-1.5 whitespace-nowrap bg-terra text-white text-[12px] font-semibold uppercase tracking-[0.12em] px-4 py-2.5 hover:bg-paper hover:text-ink transition-colors duration-300"
            >
              Start the Review
              <ArrowUpRight size={14} />
            </Link>
          </nav>
          <button
            data-testid="nav-menu-toggle"
            className={`xl:hidden p-2 -mr-2 transition-colors duration-300 ${
              darkHeader ? "text-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra" : "text-ink"
            }`}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="mobile-site-menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-site-menu"
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-40 bg-ink text-paper flex flex-col justify-center px-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <nav className="flex flex-col gap-5">
              {[{ to: "/operational-clarity-review", label: "Start the Review" }, ...LINKS].map((l, i) => (
                <motion.div
                  key={l.to + l.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-display text-2xl md:text-3xl tracking-tight hover:text-terra transition-colors duration-300"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
